﻿package org.example.project.dashboard

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.builtins.serializer
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.contentOrNull
import org.example.project.enginechat.currentUserChatIds
import org.example.project.enginechat.isOwnMessage
import org.example.project.enginechat.loadStandaloneMessages
import org.example.project.enginechat.loadStandaloneRooms
import org.example.project.engineslot.SLOT_BEREICH_WA
import org.example.project.engineslot.SLOT_BEREICH_WE
import org.example.project.engineslot.loadSlotBookings
import org.example.project.engineslot.slotBookingArtCode
import org.example.project.engineslot.slotIsBooked
import org.example.project.engineslot.slotLadenTouren
import org.example.project.engineservice.ServiceApi
import org.example.project.service.FeedControlApi
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.service.UserSettingsApi
import org.example.project.service.YardApi
import org.example.project.service.ZustandManager
import org.example.project.utils.All
import org.example.project.yard.yardPinCode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.Event
import org.w3c.dom.events.MouseEvent

internal const val MONITOR_SUMMARY_HEIGHT = 52
private const val MONITOR_FEED_MAX_HEIGHT = 248
private const val MONITOR_FEED_MAX_VISIBLE_ROWS = 6
private const val MONITOR_FEED_ROW_HEIGHT = 38
private const val MONITOR_FEED_VERTICAL_PADDING = 12
private const val MONITOR_FEED_EMPTY_HEIGHT = 44
private const val MONITOR_PUSH_PREVIEW_MS = 3400L
private const val DASHBOARD_DISMISSED_FEEDS_FILE = "dashboardfeedzustand.json"

private val dashboardMonitorJson = Json {
    prettyPrint = true
    ignoreUnknownKeys = true
}

object DashboardMonitorStyles : StyleSheet() {
    val feedScrollFrame by style {
        property("scrollbar-width", "thin")
        property("scrollbar-color", "rgba(56, 62, 74, 0.98) transparent")

        self + "::-webkit-scrollbar" style {
            width(12.px)
            height(12.px)
            backgroundColor(Color.transparent)
        }

        self + "::-webkit-scrollbar-track" style {
            backgroundColor(Color.transparent)
        }

        self + "::-webkit-scrollbar-thumb" style {
            borderRadius(999.px)
            property("background", "linear-gradient(180deg, rgba(72, 79, 92, 0.98) 0%, rgba(46, 52, 62, 0.98) 100%)")
            property("border", "1px solid rgba(255,255,255,0.10)")
        }

        self + "::-webkit-scrollbar-corner" style {
            backgroundColor(Color.transparent)
        }
    }
}

internal fun emptyDashboardMonitor(): All = All(id = "dashboardMonitor")

internal fun All.monitorCount(key: String): Int =
    module.firstOrNull { it.id == key }?.count ?: 0

private fun decodeDismissedFeedIds(raw: String): Set<String> {
    if (raw.isBlank()) return emptySet()
    val body = runCatching {
        val element = dashboardMonitorJson.parseToJsonElement(raw)
        (element as? JsonPrimitive)?.contentOrNull ?: raw
    }.getOrDefault(raw)
    return runCatching {
        dashboardMonitorJson.decodeFromString(ListSerializer(String.serializer()), body)
            .map { it.trim() }
            .filter { it.isNotBlank() }
            .toSet()
    }.getOrDefault(emptySet())
}

private suspend fun loadDismissedFeedIds(): Set<String> =
    decodeDismissedFeedIds(UserSettingsApi.loadCurrentUserSettingsFile(DASHBOARD_DISMISSED_FEEDS_FILE))

private suspend fun saveDismissedFeedIds(ids: Set<String>) {
    val body = dashboardMonitorJson.encodeToString(
        ListSerializer(String.serializer()),
        ids.filter { it.isNotBlank() }.sorted()
    )
    if (UserSettingsApi.saveCurrentUserSettingsFile(DASHBOARD_DISMISSED_FEEDS_FILE, body)) {
        GlobalManager.markJsonChanged(DASHBOARD_DISMISSED_FEEDS_FILE, "user/settings/$DASHBOARD_DISMISSED_FEEDS_FILE")
    }
}

private suspend fun pruneDismissedFeedIds(currentEntryIds: Set<String>, dismissedIds: Set<String>): Set<String> {
    val retained = dismissedIds.intersect(currentEntryIds)
    if (retained.size != dismissedIds.size) {
        saveDismissedFeedIds(retained)
    }
    return retained
}

internal suspend fun loadDashboardMonitorSnapshot(
    node: String,
    userFeedVisible: Map<String, Boolean> = emptyMap()
): All {
    if (node != DashboardNode.SYS && node != DashboardNode.CUS && node != DashboardNode.NET) {
        return emptyDashboardMonitor()
    }

    val feedEntries = mutableListOf<All>()
    val feedConfig = runCatching { FeedControlApi.loadFeedConfig() }
        .getOrDefault(DashboardFeedRegistry.defaultConfig())
    val enabledFeeds = DashboardFeedRegistry.definitions.filter { definition ->
        FeedControlApi.isFeedEnabled(feedConfig, definition.key, node) &&
            userFeedVisible[definition.key] != false
    }
    val enabledFeedKeys = enabledFeeds.map { it.key }.toSet()
    val counters = enabledFeeds.associate { it.key to 0 }.toMutableMap()

    if (DashboardFeedRegistry.FEED_SUPPORT in enabledFeedKeys && node == DashboardNode.SYS) {
        val supportTickets = runCatching { ServiceApi.loadSupport("all") }.getOrDefault(emptyList())
            .filter { it.zustand != ZustandManager.Support.ABGESCHLOSSEN }
            .sortedByDescending { it.id.toIntOrNull() ?: 0 }
        counters[DashboardFeedRegistry.FEED_SUPPORT] = supportTickets.size
        feedEntries += supportTickets.take(6).map { ticket ->
            feedEntry(
                id = "support-${ticket.id}",
                monitorKey = DashboardFeedRegistry.FEED_SUPPORT,
                icon = "images/support.svg",
                label = "Neue Support-Anfrage",
                message = ticket.problembeschreibung.ifBlank { ticket.ticketgrund }.ifBlank { ticket.supportart },
                targetKey = "SYSSupportScreen",
                sortKey = (ticket.id.toIntOrNull() ?: 0).toString().padStart(8, '0')
            )
        }
    }

    if (DashboardFeedRegistry.FEED_CR in enabledFeedKeys && node == DashboardNode.SYS) {
        val crTickets = runCatching { ServiceApi.loadCr("all") }.getOrDefault(emptyList())
            .filter { !ZustandManager.CR.isFinal(it.zustand) }
            .sortedByDescending { it.id.toIntOrNull() ?: 0 }
        counters[DashboardFeedRegistry.FEED_CR] = crTickets.size
        feedEntries += crTickets.take(6).map { ticket ->
            feedEntry(
                id = "cr-${ticket.id}",
                monitorKey = DashboardFeedRegistry.FEED_CR,
                icon = "images/cr.svg",
                label = "Neue CR-Anfrage",
                message = ticket.crBeschreibung.ifBlank { ticket.crAnforderung }.ifBlank { ticket.firma.ifBlank { ticket.firmenId } },
                targetKey = "SYSCRScreen",
                sortKey = (ticket.id.toIntOrNull() ?: 0).toString().padStart(8, '0')
            )
        }
    }

    if (DashboardFeedRegistry.FEED_CHAT in enabledFeedKeys) {
        val currentUserIds = currentUserChatIds()
        if (currentUserIds.isNotEmpty()) {
            val rooms = runCatching { loadStandaloneRooms(node, currentUserIds) }.getOrDefault(emptyList())
            val unreadRoomsByAuthor = linkedMapOf<String, All>()
            rooms.forEach { room ->
                val lastMessage = runCatching { loadStandaloneMessages(node, room.id) }
                    .getOrDefault(emptyList())
                    .lastOrNull()
                    ?: return@forEach
                if (isOwnMessage(lastMessage, currentUserIds)) return@forEach
                val authorId = lastMessage.erstelltVon.trim()
                val authorLabel = authorId.takeIf { it.isNotBlank() }?.let { "Chat:" }.orEmpty().ifBlank { "Chat:" }
                val entry = feedEntry(
                    id = "chat-${authorId.ifBlank { room.id }}",
                    monitorKey = DashboardFeedRegistry.FEED_CHAT,
                    icon = "images/chat.svg",
                    label = authorLabel,
                    message = lastMessage.bemerkung.ifBlank { room.lastMessage }.ifBlank { room.name.ifBlank { "Chat" } },
                    targetKey = "ChatModuleScreen",
                    sortKey = lastMessage.timestamp
                        .ifBlank { room.updatedAt }
                        .ifBlank { room.id }
                )
                val current = unreadRoomsByAuthor[authorLabel]
                if (current == null || entry.sortKey > current.sortKey) {
                    unreadRoomsByAuthor[authorLabel] = entry
                }
            }
            val unreadRooms = unreadRoomsByAuthor.values.toList()
            counters[DashboardFeedRegistry.FEED_CHAT] = unreadRooms.size
            feedEntries += unreadRooms
        }
    }

    if (DashboardFeedRegistry.FEED_SLOT in enabledFeedKeys) {
        val firmenId = LoginService.getUser()?.firmenId.orEmpty().trim()
            .ifBlank { GlobalManager.currentFirmenId.trim() }
        if (firmenId.isNotBlank()) {
            val slotBookings = runCatching { loadSlotBookings(firmenId) }.getOrDefault(emptyList())
                .filter(::slotIsBooked)
                .sortedWith(
                    compareByDescending<All> { it.datum }
                        .thenByDescending { it.uhrzeitVon }
                        .thenByDescending { it.id.toIntOrNull() ?: 0 }
                )
            val slotFeedEntries = slotBookings.flatMap { booking ->
                val artCode = slotBookingArtCode(booking)
                when (artCode) {
                    SLOT_BEREICH_WA -> {
                        val touren = slotLadenTouren(booking).ifEmpty { listOf(booking) }
                        touren.mapIndexed { tourIndex, tour ->
                            feedEntry(
                                id = "slot-${booking.firmenId}-${booking.auftragsart}-${booking.id}-tour-$tourIndex-${tour.lieferscheinnummer}",
                                monitorKey = DashboardFeedRegistry.FEED_SLOT,
                                icon = "images/slotverwaltung.svg",
                                label = dashboardSlotFeedLabel(booking, artCode),
                                message = dashboardSlotFeedMessage(tour, artCode),
                                targetKey = "SlotView",
                                sortKey = "${booking.datum} ${booking.uhrzeitVon} ${booking.id.padStart(8, '0')} ${tourIndex.toString().padStart(3, '0')}",
                                messageParts = dashboardSlotFeedMessageParts(tour, artCode)
                            )
                        }
                    }

                    else -> listOf(
                        feedEntry(
                            id = "slot-${booking.firmenId}-${booking.auftragsart}-${booking.id}",
                            monitorKey = DashboardFeedRegistry.FEED_SLOT,
                            icon = "images/slotverwaltung.svg",
                            label = dashboardSlotFeedLabel(booking, artCode),
                            message = dashboardSlotFeedMessage(booking, artCode),
                            targetKey = "SlotView",
                            sortKey = "${booking.datum} ${booking.uhrzeitVon} ${booking.id.padStart(8, '0')}",
                            messageParts = dashboardSlotFeedMessageParts(booking, artCode)
                        )
                    )
                }
            }
            counters[DashboardFeedRegistry.FEED_SLOT] = slotFeedEntries.size
            feedEntries += slotFeedEntries.take(6)
        }
    }

    if (DashboardFeedRegistry.FEED_YARD in enabledFeedKeys) {
        val firmenId = LoginService.getUser()?.firmenId.orEmpty().trim()
            .ifBlank { GlobalManager.currentFirmenId.trim() }
        if (firmenId.isNotBlank()) {
            val yardFeedEntries = dashboardYardVorgangslogFeedEntries(firmenId)
            counters[DashboardFeedRegistry.FEED_YARD] = yardFeedEntries.size
            feedEntries += yardFeedEntries.take(8)
        }
    }

    val sortedEntries = feedEntries
        .sortedByDescending { it.sortKey }
        .take(12)
    val dismissedFeedIds = pruneDismissedFeedIds(
        currentEntryIds = sortedEntries.map { it.id }.toSet(),
        dismissedIds = loadDismissedFeedIds()
    )
    val userVisibleEntries = sortedEntries.filterNot { it.id in dismissedFeedIds }
    val userVisibleCounts = enabledFeeds.associate { it.key to 0 }.toMutableMap()
    userVisibleEntries.forEach { entry ->
        val key = dashboardFeedKeyForEntry(entry)
        if (key.isNotBlank()) {
            userVisibleCounts[key] = (userVisibleCounts[key] ?: 0) + 1
        }
    }

    return All(
        id = "dashboardMonitor",
        module = enabledFeeds.map { definition ->
            counterChip(
                id = definition.key,
                icon = definition.icon,
                count = userVisibleCounts[definition.key] ?: 0,
                targetKey = definition.targetKey
            )
        },
        columnSettings = userVisibleEntries
    )
}

@Composable
internal fun DashboardMonitorDrawer(
    snapshot: All,
    pushEnabledByFeed: Map<String, Boolean> = emptyMap(),
    embeddedButton: Boolean = false,
    leadingContent: (@Composable () -> Unit)? = null,
    onOpenTarget: (String) -> Unit
) {
    Style(DashboardMonitorStyles)

    val scope = rememberCoroutineScope()
    var layerHeight by remember { mutableStateOf(MONITOR_SUMMARY_HEIGHT.toDouble()) }
    var dragging by remember { mutableStateOf(false) }
    var dragStartY by remember { mutableStateOf(0.0) }
    var dragStartHeight by remember { mutableStateOf(MONITOR_SUMMARY_HEIGHT.toDouble()) }
    var previousEntryVersions by remember { mutableStateOf(mapOf<String, String>()) }
    var glowingKeys by remember { mutableStateOf(setOf<String>()) }
    var transientEntry by remember { mutableStateOf<All?>(null) }
    var dismissedEntryIds by remember { mutableStateOf(setOf<String>()) }

    fun dismissEntry(entry: All) {
        val next = dismissedEntryIds + entry.id
        dismissedEntryIds = next
        glowingKeys = glowingKeys - dashboardFeedKeyForEntry(entry)
        transientEntry = transientEntry?.takeIf { it.id != entry.id }
        scope.launch { saveDismissedFeedIds(next) }
    }

    LaunchedEffect(Unit) {
        dismissedEntryIds = loadDismissedFeedIds()
    }

    val visibleEntries = remember(snapshot.columnSettings, dismissedEntryIds) {
        snapshot.columnSettings.filterNot { it.id in dismissedEntryIds }
    }
    val visibleCounts = remember(snapshot.module, visibleEntries) {
        buildMap {
            snapshot.module.forEach { item -> put(item.id, 0) }
            visibleEntries.forEach { entry ->
                val key = dashboardFeedKeyForEntry(entry)
                if (key.isNotBlank()) {
                    put(key, (get(key) ?: 0) + 1)
                }
            }
        }
    }
    val summaryItems = remember(snapshot.module, visibleCounts) {
        snapshot.module.map { item ->
            item.copy(count = visibleCounts[item.id] ?: 0)
        }
    }

    val visibleEntryVersions = remember(visibleEntries) {
        visibleEntries.associate { entry ->
            entry.id to monitorEntryVersion(entry)
        }
    }
    val changedVisibleEntries = remember(visibleEntries, visibleEntryVersions, previousEntryVersions) {
        visibleEntries.filter { entry ->
            visibleEntryVersions[entry.id] != previousEntryVersions[entry.id]
        }
    }
    val changedPushEntries = remember(changedVisibleEntries, pushEnabledByFeed) {
        changedVisibleEntries.filter { entry ->
            pushEnabledByFeed[dashboardFeedKeyForEntry(entry)] != false
        }
    }

    LaunchedEffect(visibleEntryVersions, pushEnabledByFeed) {
        val newKeys = changedPushEntries
            .map(::dashboardFeedKeyForEntry)
            .filter { it.isNotBlank() }
            .toSet()
        if (newKeys.isNotEmpty()) {
            glowingKeys = glowingKeys + newKeys
            transientEntry = changedPushEntries
                .maxByOrNull { it.sortKey }
        }
        previousEntryVersions = visibleEntryVersions
    }

    LaunchedEffect(transientEntry?.id) {
        val currentEntry = transientEntry
        if (currentEntry != null) {
            delay(MONITOR_PUSH_PREVIEW_MS)
            if (transientEntry?.id == currentEntry.id) {
                glowingKeys = glowingKeys - dashboardFeedKeyForEntry(currentEntry)
                transientEntry = null
            }
        }
    }

    val baseHeight = MONITOR_SUMMARY_HEIGHT.toDouble()
    val expandedFeedHeight = if (visibleEntries.isEmpty()) {
        MONITOR_FEED_EMPTY_HEIGHT
    } else {
        visibleEntries.size.coerceAtMost(MONITOR_FEED_MAX_VISIBLE_ROWS) * MONITOR_FEED_ROW_HEIGHT +
            MONITOR_FEED_VERTICAL_PADDING
    }.coerceAtMost(MONITOR_FEED_MAX_HEIGHT - MONITOR_SUMMARY_HEIGHT)
    val expandedLayerHeight = (MONITOR_SUMMARY_HEIGHT + expandedFeedHeight).toDouble()
    val liveHeight = layerHeight.coerceIn(baseHeight, expandedLayerHeight)
    val feedVisible = liveHeight > baseHeight + 12
    val summaryPushVisible = !feedVisible && transientEntry != null
    val hasVisibleFeeds = visibleEntries.isNotEmpty()

    LaunchedEffect(hasVisibleFeeds, expandedLayerHeight) {
        if (!hasVisibleFeeds) {
            layerHeight = MONITOR_SUMMARY_HEIGHT.toDouble()
        } else if (feedVisible) {
            layerHeight = expandedLayerHeight
        }
    }

    val dragState by rememberUpdatedState(
        object {
            val currentDragging = dragging
            val currentStartY = dragStartY
            val currentStartHeight = dragStartHeight
            fun setLayerHeight(next: Double) {
                layerHeight = next.coerceIn(baseHeight, expandedLayerHeight)
            }
            fun stopDragging() {
                dragging = false
            }
        }
    )

    DisposableEffect(Unit) {
        val moveListener: (Event) -> Unit = { ev ->
            val event = ev as MouseEvent
            if (dragState.currentDragging) {
                val delta = event.clientY.toDouble() - dragState.currentStartY
                dragState.setLayerHeight(dragState.currentStartHeight + delta)
            }
        }
        val upListener: (Event) -> Unit = { dragState.stopDragging() }
        val leaveListener: (Event) -> Unit = {
            if (dragState.currentDragging) dragState.stopDragging()
        }
        document.addEventListener("mousemove", moveListener)
        document.addEventListener("mouseup", upListener)
        window.addEventListener("mouseleave", leaveListener)
        onDispose {
            document.removeEventListener("mousemove", moveListener)
            document.removeEventListener("mouseup", upListener)
            window.removeEventListener("mouseleave", leaveListener)
        }
    }

    if (embeddedButton) {
        Div({
            style {
                property("width", "max-content")
                property("max-width", "calc(100vw - 40px)")
                height(MONITOR_SUMMARY_HEIGHT.px)
                position(Position.Relative)
                backgroundColor(Color.transparent)
                property("border-bottom", "1px solid ${DashboardTheme.BORDER_LIGHT}")
                property("box-sizing", "border-box")
                overflow("visible")
                property("pointer-events", "auto")
                property("transition", "border-color 0.24s ease")
            }
        }) {
            DashboardMonitorSummaryButton(
                summaryItems = summaryItems,
                glowingKeys = glowingKeys,
                summaryPushVisible = summaryPushVisible,
                transientEntry = transientEntry,
                compact = true,
                leadingContent = leadingContent,
                underlined = true,
                showFeedArrow = hasVisibleFeeds,
                feedExpanded = feedVisible,
                onToggleFeed = {
                    layerHeight = if (feedVisible) {
                        MONITOR_SUMMARY_HEIGHT.toDouble()
                    } else {
                        expandedLayerHeight
                    }
                }
            )

            if (feedVisible) {
                Div({
                    classes(DashboardMonitorStyles.feedScrollFrame)
                    style {
                        position(Position.Absolute)
                        right(0.px)
                        top(MONITOR_SUMMARY_HEIGHT.px)
                        property("z-index", "9700")
                        property("width", "min(760px, calc(100vw - 40px))")
                        property("max-width", "calc(100vw - 40px)")
                        height((liveHeight - MONITOR_SUMMARY_HEIGHT - 6).px)
                        overflowY("auto")
                        overflowX("hidden")
                        padding(8.px, 10.px, 4.px, 10.px)
                        boxSizing("border-box")
                        property("border-top", "1px solid ${DashboardTheme.BORDER_LIGHT}")
                        backgroundColor(Color("rgba(255,255,255,0.03)"))
                        property("backdrop-filter", "blur(18px)")
                        property("box-shadow", "0 14px 34px ${DashboardTheme.SURFACE_SHADOW}")
                    }
                }) {
                    if (visibleEntries.isEmpty()) {
                        Div({
                            style {
                                padding(12.px, 10.px)
                                color(DashboardTheme.TEXT_MUTED)
                                fontSize(12.px)
                                property("text-align", "center")
                            }
                        }) { Text("Keine Live-Ereignisse") }
                    } else {
                        visibleEntries.forEachIndexed { index, entry ->
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    gap(10.px)
                                    width(100.percent)
                                    minWidth(0.px)
                                    padding(9.px, 10.px)
                                    property("box-sizing", "border-box")
                                    if (entry.id in glowingKeys) {
                                        backgroundColor(Color("rgba(255,255,255,0.03)"))
                                    }
                                    if (index < visibleEntries.lastIndex) {
                                        property("border-bottom", "1px solid ${DashboardTheme.BORDER_LIGHT}")
                                    }
                                }
                                onClick {
                                    glowingKeys = glowingKeys - dashboardFeedKeyForEntry(entry)
                                }
                            }) {
                                Img(src = "images/openrun.svg", attrs = {
                                    style {
                                        width(15.px)
                                        height(15.px)
                                        flexShrink(0)
                                        cursor("pointer")
                                        property("filter", DashboardTheme.ICON_FILTER_WHITE)
                                    }
                                    onClick { event ->
                                        event.stopPropagation()
                                        glowingKeys = glowingKeys - dashboardFeedKeyForEntry(entry)
                                        transientEntry = transientEntry?.takeIf { it.id != entry.id }
                                        onOpenTarget(entry.screenKey)
                                    }
                                })
                                Img(src = entry.icon, attrs = {
                                    style {
                                        width(15.px)
                                        height(15.px)
                                        flexShrink(0)
                                        property("filter", DashboardTheme.ICON_FILTER_WHITE)
                                    }
                                })
                                Span({
                                    style {
                                        color(DashboardTheme.TEXT_LIGHT)
                                        fontSize(13.px)
                                        fontWeight("700")
                                        flexShrink(0)
                                    }
                                }) { Text(entry.label) }
                                DashboardFeedMessageLine(entry, 13)
                                Img(src = "images/close.svg", attrs = {
                                    style {
                                        width(14.px)
                                        height(14.px)
                                        flexShrink(0)
                                        cursor("pointer")
                                        property("filter", DashboardTheme.ICON_FILTER_WHITE)
                                    }
                                    onClick { event ->
                                        event.stopPropagation()
                                        dismissEntry(entry)
                                    }
                                })
                            }
                        }
                    }
                }
            }
        }
        return
    }

    Div({
        style {
            width(100.percent)
            maxWidth(940.px)
            height(liveHeight.px)
            property("background", DashboardTheme.PREMIUM_SURFACE_BG)
            property("backdrop-filter", "blur(18px)")
            property("border-left", "1px solid ${DashboardTheme.BORDER_LIGHT}")
            property("border-right", "1px solid ${DashboardTheme.BORDER_LIGHT}")
            property("border-bottom", "1px solid ${DashboardTheme.BORDER_LIGHT}")
            property("border-top", "0")
            property("border-top-left-radius", "0")
            property("border-top-right-radius", "0")
            property("border-bottom-left-radius", "24px")
            property("border-bottom-right-radius", "24px")
            property("box-shadow", "0 14px 34px ${DashboardTheme.SURFACE_SHADOW}")
            property("box-sizing", "border-box")
            overflow("hidden")
            property("pointer-events", "auto")
            property("transition", if (dragging) "none" else "height 0.22s ease, box-shadow 0.22s ease, border-bottom-color 0.22s ease")
        }
    }) {
        if (feedVisible) {
            Div({
                classes(DashboardMonitorStyles.feedScrollFrame)
                style {
                    height((liveHeight - MONITOR_SUMMARY_HEIGHT).px)
                    overflowY("auto")
                    padding(12.px, 12.px, 6.px, 12.px)
                    boxSizing("border-box")
                }
            }) {
                if (visibleEntries.isEmpty()) {
                    Div({
                        style {
                            padding(14.px, 10.px)
                            color(DashboardTheme.TEXT_MUTED)
                            fontSize(12.px)
                            property("text-align", "center")
                        }
                    }) { Text("Keine Live-Ereignisse") }
                } else {
                    visibleEntries.forEachIndexed { index, entry ->
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(10.px)
                                padding(10.px, 12.px)
                                property("box-sizing", "border-box")
                                if (entry.id in glowingKeys) {
                                    backgroundColor(Color("rgba(255,255,255,0.03)"))
                                }
                                if (index < visibleEntries.lastIndex) {
                                    property("border-bottom", "1px solid ${DashboardTheme.BORDER_LIGHT}")
                                }
                            }
                            onClick {
                                glowingKeys = glowingKeys - dashboardFeedKeyForEntry(entry)
                            }
                        }) {
                            Img(src = "images/openrun.svg", attrs = {
                                style {
                                    width(15.px)
                                    height(15.px)
                                    flexShrink(0)
                                    cursor("pointer")
                                    property("filter", DashboardTheme.ICON_FILTER_WHITE)
                                }
                                onClick { event ->
                                    event.stopPropagation()
                                    glowingKeys = glowingKeys - dashboardFeedKeyForEntry(entry)
                                    transientEntry = transientEntry?.takeIf { it.id != entry.id }
                                    onOpenTarget(entry.screenKey)
                                }
                            })
                            Img(src = entry.icon, attrs = {
                                style {
                                    width(15.px)
                                    height(15.px)
                                    flexShrink(0)
                                    property("filter", DashboardTheme.ICON_FILTER_WHITE)
                                }
                            })
                            Span({
                                style {
                                    color(DashboardTheme.TEXT_LIGHT)
                                    fontSize(14.px)
                                    fontWeight("700")
                                    flexShrink(0)
                                }
                            }) { Text(entry.label) }
                            DashboardFeedMessageLine(entry, 14, fullText = true)
                            Img(src = "images/close.svg", attrs = {
                                style {
                                    width(14.px)
                                    height(14.px)
                                    flexShrink(0)
                                    cursor("pointer")
                                    property("filter", DashboardTheme.ICON_FILTER_WHITE)
                                }
                                onClick { event ->
                                    event.stopPropagation()
                                    dismissEntry(entry)
                                }
                            })
                        }
                    }
                }
            }
        }

        Div({
            style {
                position(Position.Absolute)
                left(0.px)
                right(0.px)
                bottom(0.px)
                height(MONITOR_SUMMARY_HEIGHT.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                gap(0.px)
                padding(0.px, 18.px)
                property("background", "linear-gradient(180deg, rgba(52, 58, 68, 0.98) 0%, rgba(31, 35, 42, 1) 100%)")
                property("border-top", if (feedVisible) "1px solid ${DashboardTheme.BORDER_LIGHT}" else "0")
                cursor("ns-resize")
                boxSizing("border-box")
            }
            onMouseDown { event ->
                dragStartY = event.clientY.toDouble()
                dragStartHeight = liveHeight
                dragging = true
            }
        }) {
            Div({
                style {
                    position(Position.Relative)
                    width(100.percent)
                    height(100.percent)
                }
            }) {
                Div({
                    style {
                        position(Position.Absolute)
                        left(0.px)
                        right(0.px)
                        top(0.px)
                        bottom(0.px)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        gap(0.px)
                        opacity(if (summaryPushVisible) 0 else 1)
                        property("transform", if (summaryPushVisible) "translateY(-10px)" else "translateY(0)")
                        property("transition", "opacity 0.22s ease, transform 0.22s ease")
                    }
                }) {
                    summaryItems.forEachIndexed { index, item ->
                        val count = item.count
                        val glowing = item.id in glowingKeys && count > 0
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(6.px)
                                padding(0.px, 10.px)
                                opacity(if (count > 0) 1 else 0.58)
                            }
                        }) {
                            Img(src = item.icon, attrs = {
                                style {
                                    width(18.px)
                                    height(18.px)
                                    property("filter", if (count > 0) DashboardTheme.ICON_FILTER_ACTIVE else DashboardTheme.ICON_FILTER_WHITE)
                                }
                            })
                            Span({
                                style {
                                    minWidth(16.px)
                                    height(22.px)
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    justifyContent(JustifyContent.FlexStart)
                                    color(if (glowing) DashboardTheme.ACCENT else DashboardTheme.TEXT_LIGHT)
                                    fontSize(13.px)
                                    fontWeight("700")
                                    boxSizing("border-box")
                                    if (glowing) {
                                        property("text-shadow", "0 0 8px rgba(255,255,255,0.18)")
                                    }
                                }
                            }) { Text(count.toString()) }
                        }
                        if (index < summaryItems.lastIndex) {
                            Div({
                                style {
                                    width(1.px)
                                    height(20.px)
                                    backgroundColor(DashboardTheme.BORDER_LIGHT)
                                    marginLeft(2.px)
                                    marginRight(2.px)
                                }
                            })
                        }
                    }
                }
                transientEntry?.let { entry ->
                    Div({
                        style {
                            position(Position.Absolute)
                            left(0.px)
                            top(0.px)
                            bottom(0.px)
                            property("width", "max-content")
                            property("min-width", "100%")
                            property("max-width", "calc(100vw - 40px)")
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.FlexStart)
                            gap(10.px)
                            padding(0.px, 16.px)
                            boxSizing("border-box")
                            opacity(if (summaryPushVisible) 1 else 0)
                            property("transform", if (summaryPushVisible) "translateY(0)" else "translateY(10px)")
                            property("transition", "opacity 0.24s ease, transform 0.24s ease")
                            overflow("visible")
                        }
                    }) {
                        Img(src = entry.icon, attrs = {
                            style {
                                width(18.px)
                                height(18.px)
                                flexShrink(0)
                                property("filter", DashboardTheme.ICON_FILTER_WHITE)
                            }
                        })
                        Span({
                            style {
                                color(DashboardTheme.TEXT_LIGHT)
                                fontSize(13.px)
                                fontWeight("700")
                                flexShrink(0)
                                property("white-space", "nowrap")
                            }
                        }) { Text(entry.label) }
                        Div({
                            style {
                                width(1.px)
                                height(18.px)
                                backgroundColor(DashboardTheme.BORDER_LIGHT)
                                flexShrink(0)
                            }
                        })
                        DashboardFeedMessageLine(entry, 13, fullText = true)
                    }
                }
            }
        }
    }
}

@Composable
private fun DashboardMonitorSummaryButton(
    summaryItems: List<All>,
    glowingKeys: Set<String>,
    summaryPushVisible: Boolean,
    transientEntry: All?,
    compact: Boolean,
    leadingContent: (@Composable () -> Unit)? = null,
    underlined: Boolean = false,
    showFeedArrow: Boolean = false,
    feedExpanded: Boolean = false,
    onToggleFeed: (() -> Unit)? = null
) {
    Div({
        style {
            height(MONITOR_SUMMARY_HEIGHT.px)
            position(Position.Relative)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(if (leadingContent == null) JustifyContent.Center else JustifyContent.FlexStart)
            property("width", "max-content")
            padding(0.px, if (compact) 12.px else 18.px)
            if (underlined) {
                backgroundColor(Color.transparent)
            } else {
                property("background", "linear-gradient(180deg, rgba(52, 58, 68, 0.98) 0%, rgba(31, 35, 42, 1) 100%)")
            }
            boxSizing("border-box")
            overflow("visible")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(if (leadingContent == null) JustifyContent.Center else JustifyContent.FlexStart)
                gap(if (leadingContent == null) 0.px else 12.px)
                property("width", "max-content")
                opacity(if (summaryPushVisible) 0 else 1)
                property("transform", if (summaryPushVisible) "translateY(-10px)" else "translateY(0)")
                property("transition", "opacity 0.22s ease, transform 0.22s ease")
                boxSizing("border-box")
            }
        }) {
            if (leadingContent != null) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        property("width", "max-content")
                        flexShrink(0)
                    }
                }) {
                    leadingContent()
                }
            }
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    gap(0.px)
                    flexShrink(0)
                }
            }) {
                summaryItems.forEachIndexed { index, item ->
                    val count = item.count
                    val glowing = item.id in glowingKeys && count > 0
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(6.px)
                            padding(0.px, if (compact) 7.px else 10.px)
                            opacity(if (count > 0) 1 else 0.58)
                        }
                    }) {
                        Img(src = item.icon, attrs = {
                            style {
                                width(18.px)
                                height(18.px)
                                property("filter", if (count > 0) DashboardTheme.ICON_FILTER_ACTIVE else DashboardTheme.ICON_FILTER_WHITE)
                            }
                        })
                        Span({
                            style {
                                minWidth(16.px)
                                height(22.px)
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.FlexStart)
                                color(if (glowing) DashboardTheme.ACCENT else DashboardTheme.TEXT_LIGHT)
                                fontSize(13.px)
                                fontWeight("700")
                                boxSizing("border-box")
                                if (glowing) {
                                    property("text-shadow", "0 0 8px rgba(255,255,255,0.18)")
                                }
                            }
                        }) { Text(count.toString()) }
                    }
                    if (index < summaryItems.lastIndex) {
                        Div({
                            style {
                                width(1.px)
                                height(20.px)
                                backgroundColor(DashboardTheme.BORDER_LIGHT)
                                marginLeft(2.px)
                                marginRight(2.px)
                            }
                        })
                    }
                }
            }
            if (showFeedArrow && onToggleFeed != null) {
                Img(src = if (feedExpanded) "images/up.svg" else "images/down.svg", attrs = {
                    style {
                        width(17.px)
                        height(17.px)
                        marginLeft(8.px)
                        flexShrink(0)
                        cursor("pointer")
                        property("filter", DashboardTheme.ICON_FILTER_WHITE)
                        property("transition", "transform 0.2s ease, opacity 0.18s ease")
                    }
                    onClick { event ->
                        event.stopPropagation()
                        onToggleFeed()
                    }
                })
            }
        }
        transientEntry?.let { entry ->
            Div({
                style {
                    position(Position.Absolute)
                    left(0.px)
                    top(0.px)
                    bottom(0.px)
                    property("width", "max-content")
                    property("min-width", "100%")
                    property("max-width", "calc(100vw - 40px)")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.FlexStart)
                    gap(10.px)
                    padding(0.px, if (leadingContent == null) 16.px else 14.px)
                    boxSizing("border-box")
                    opacity(if (summaryPushVisible) 1 else 0)
                    property("transform", if (summaryPushVisible) "translateY(0)" else "translateY(10px)")
                    property("transition", "opacity 0.24s ease, transform 0.24s ease")
                    overflow("visible")
                }
            }) {
                Img(src = entry.icon, attrs = {
                    style {
                        width(18.px)
                        height(18.px)
                        flexShrink(0)
                        property("filter", DashboardTheme.ICON_FILTER_WHITE)
                    }
                })
                Span({
                    style {
                        color(DashboardTheme.TEXT_LIGHT)
                        fontSize(13.px)
                        fontWeight("700")
                        flexShrink(0)
                        property("white-space", "nowrap")
                    }
                }) { Text(entry.label) }
                Div({
                    style {
                        width(1.px)
                        height(18.px)
                        backgroundColor(DashboardTheme.BORDER_LIGHT)
                        flexShrink(0)
                    }
                })
                DashboardFeedMessageLine(entry, 13, fullText = true)
            }
        }
    }
}

private fun counterChip(
    id: String,
    icon: String,
    count: Int,
    targetKey: String
): All = All(
    id = id,
    icon = icon,
    screenKey = targetKey,
    count = count
)

private fun feedEntry(
    id: String,
    monitorKey: String,
    icon: String,
    label: String,
    message: String,
    targetKey: String,
    sortKey: String,
    messageParts: Map<String, String> = emptyMap()
): All = All(
    id = id,
    icon = icon,
    label = label,
    bemerkung = message,
    screenKey = targetKey,
    sortKey = sortKey,
    monitorKey = monitorKey,
    messageIcon1 = messageParts["messageIcon1"].orEmpty(),
    messageText1 = messageParts["messageText1"].orEmpty(),
    messageIcon2 = messageParts["messageIcon2"].orEmpty(),
    messageText2 = messageParts["messageText2"].orEmpty(),
    messageIcon3 = messageParts["messageIcon3"].orEmpty(),
    messageText3 = messageParts["messageText3"].orEmpty()
)

internal fun dashboardFeedKeyForEntry(entry: All): String =
    entry.monitorKey.ifBlank {
        entry.id.substringBefore("-").takeIf { DashboardFeedRegistry.definition(it) != null }.orEmpty()
    }

private fun monitorEntryVersion(entry: All): String =
    "${entry.sortKey}|${entry.label}|${entry.bemerkung}"

private fun dashboardSlotFeedLabel(booking: All, artCode: String): String =
    when (artCode) {
        SLOT_BEREICH_WE -> "WE Angemeldet"
        SLOT_BEREICH_WA -> "WA Angemeldet"
        else -> "Slot ${booking.slotIndex}"
    }

private fun dashboardSlotFeedMessage(booking: All, artCode: String): String =
    when (artCode) {
        SLOT_BEREICH_WE -> if (booking.zustand >= ZustandManager.TOR_ZUGEWIESEN && booking.tor != null) {
            "${booking.lkwkennzeichen.ifBlank { "LKW" }} an Tor ${booking.tor} zugewiesen"
        } else {
            listOf("LS: ${booking.lieferscheinnummer.ifBlank { "offen" }}", booking.lieferant)
                .filter { it.isNotBlank() }
                .joinToString(" ")
        }

        SLOT_BEREICH_WA -> listOf("LS: ${booking.lieferscheinnummer.ifBlank { "offen" }}", booking.empfaenger)
            .filter { it.isNotBlank() }
            .joinToString(" ")

        else -> "Slot ${booking.slotIndex}"
    }

private fun dashboardSlotFeedMessageParts(booking: All, artCode: String): Map<String, String> =
    when (artCode) {
        SLOT_BEREICH_WE -> if (booking.zustand >= ZustandManager.TOR_ZUGEWIESEN && booking.tor != null) {
            dashboardFeedMessageParts(
                "images/lkw.svg" to booking.lkwkennzeichen.ifBlank { "LKW" },
                "images/toruebersicht.svg" to "Tor ${booking.tor} zugewiesen"
            )
        } else {
            dashboardFeedMessageParts(
                "images/order.svg" to "LS: ${booking.lieferscheinnummer.ifBlank { "offen" }}",
                "images/domain.svg" to booking.lieferant
            )
        }

        SLOT_BEREICH_WA -> dashboardFeedMessageParts(
            "images/order.svg" to "LS: ${booking.lieferscheinnummer.ifBlank { "offen" }}",
            "images/zielort.svg" to booking.empfaenger
        )

        else -> emptyMap()
    }

private fun dashboardFeedMessageParts(vararg parts: Pair<String, String>): Map<String, String> =
    parts
        .filter { (_, text) -> text.isNotBlank() }
        .flatMapIndexed { index, (icon, text) ->
            listOf(
                "messageIcon${index + 1}" to icon,
                "messageText${index + 1}" to text
            )
        }
        .toMap()

private suspend fun dashboardYardVorgangslogFeedEntries(firmenId: String): List<All> {
    val logs = mutableListOf<All>()

    runCatching { YardApi.loadWeListe(firmenId) }
        .getOrDefault(emptyList())
        .filter { it.lfdNr > 0 }
        .distinctBy { "${it.firmenId}:WE:${it.lfdNr}" }
        .forEach { auftrag ->
            logs += runCatching { YardApi.loadWeVorgangslog(auftrag.lfdNr, auftrag.firmenId.ifBlank { firmenId }) }
                .getOrDefault(emptyList())
        }

    runCatching { YardApi.loadWaLkwListe(firmenId) }
        .getOrDefault(emptyList())
        .filter { it.lieferscheinnummer.isNotBlank() }
        .distinctBy { "${it.firmenId}:WA:${it.lieferscheinnummer}" }
        .forEach { lkw ->
            logs += runCatching { YardApi.loadWaVorgangslog(lkw.lieferscheinnummer, lkw.firmenId.ifBlank { firmenId }) }
                .getOrDefault(emptyList())
        }

    return logs
        .distinctBy { log ->
            log.id.ifBlank {
                "${dashboardYardLogTimestamp(log)}:${log.typ}:${log.notiz}:${log.lfdNr}:${log.lieferscheinnummer}"
            }
        }
        .sortedByDescending { dashboardYardLogTimestamp(it) }
        .mapIndexed { index, log ->
            val timestamp = dashboardYardLogTimestamp(log)
            val typ = dashboardYardAuftragsTyp(log)
            feedEntry(
                id = "yard-$typ-${log.firmenId}-${log.lfdNr}-${log.lieferscheinnummer}-${timestamp}-$index",
                monitorKey = DashboardFeedRegistry.FEED_YARD,
                icon = if (typ == "WA") "images/wa.svg" else "images/we.svg",
                label = "$typ ${dashboardYardEventLabel(log)}",
                message = dashboardYardMessage(log),
                targetKey = "LeitstandHofScreen",
                sortKey = timestamp.toString().padStart(16, '0'),
                messageParts = dashboardYardMessageParts(log)
            )
        }
}

private fun dashboardYardAuftragsTyp(entry: All): String =
    entry.auftragsart.trim().uppercase()
        .ifBlank { entry.bereich.trim().uppercase() }
        .takeIf { it == "WE" || it == "WA" }
        ?: if (entry.lieferscheinnummer.isNotBlank()) "WA" else "WE"

private fun dashboardYardEventLabel(entry: All): String =
    when (entry.typ.trim().uppercase()) {
        "LIEFERUNG_ANGEMELDET" -> "angemeldet"
        "ABHOLUNG_ANGEMELDET" -> "angemeldet"
        "LKW_EINGETROFFEN" -> "eingetroffen"
        "TOR_ZUGEWIESEN" -> "Tor zugewiesen"
        "AM_TOR" -> "am Tor"
        "PIN_ABFRAGE" -> "PIN-Abfrage"
        "PIN_BESTAETIGT" -> "PIN bestätigt"
        "TOR_ZUWEISUNG_FEHLT" -> "Tor fehlt"
        else -> entry.typ.ifBlank { ZustandManager.textFor(entry.zustand) }
    }

private fun dashboardYardMessage(entry: All): String =
    entry.notiz.ifBlank {
        listOf(
            entry.lkwkennzeichen.ifBlank { "LKW" },
            entry.tor?.let { "Tor $it" }.orEmpty(),
            ZustandManager.textFor(entry.zustand)
        ).filter { it.isNotBlank() }.joinToString(" - ")
    }

private fun dashboardYardMessageParts(entry: All): Map<String, String> =
    dashboardFeedMessageParts(
        "images/order.svg" to entry.lieferscheinnummer.ifBlank { "PIN ${yardPinCode(entry)}" },
        "images/lkw.svg" to entry.lkwkennzeichen,
        "images/toruebersicht.svg" to entry.tor?.let { "Tor $it" }.orEmpty()
    )

private fun dashboardYardLogTimestamp(entry: All): Long =
    entry.timestamp.toLongOrNull()
        ?: entry.id.toLongOrNull()
        ?: 0L

@Composable
private fun DashboardFeedMessageLine(entry: All, fontSizePx: Int, fullText: Boolean = false) {
    val parts = listOf(
        entry.messageIcon1 to entry.messageText1,
        entry.messageIcon2 to entry.messageText2,
        entry.messageIcon3 to entry.messageText3
    ).mapNotNull { (icon, text) ->
        if (text.isBlank()) null else icon to text
    }
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(10.px)
            minWidth(0.px)
            if (fullText) {
                property("width", "max-content")
                flexShrink(0)
            } else {
                flex(1)
                overflow("hidden")
            }
        }
    }) {
        if (parts.isEmpty()) {
            Span({
                style {
                    color(DashboardTheme.TEXT_MUTED)
                    fontSize(fontSizePx.px)
                    property("white-space", "nowrap")
                    if (fullText) {
                        flexShrink(0)
                    } else {
                        overflow("hidden")
                        property("text-overflow", "ellipsis")
                        flex(1)
                    }
                }
            }) { Text(entry.bemerkung.ifBlank { "Neues Ereignis" }) }
        } else {
            parts.forEach { (icon, text) ->
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(5.px)
                        minWidth(0.px)
                        flexShrink(if (fullText) 0 else 1)
                    }
                }) {
                    if (icon.isNotBlank()) {
                        Img(src = icon, attrs = {
                            style {
                                width(14.px)
                                height(14.px)
                                flexShrink(0)
                                property("filter", DashboardTheme.ICON_FILTER_WHITE)
                            }
                        })
                    }
                    Span({
                        style {
                            color(DashboardTheme.TEXT_MUTED)
                            fontSize(fontSizePx.px)
                            property("white-space", "nowrap")
                            if (!fullText) {
                                overflow("hidden")
                                property("text-overflow", "ellipsis")
                            }
                        }
                    }) { Text(text) }
                }
            }
        }
    }
}
